<template>
    <span class="VInput">
        <span class="VInput__prefix">
            <slot name="prefix">

            </slot>
        </span>
        <span class="VInput-wrapper">
            <input
                :placeholder="placeholder"
                v-model="localValue"
                :disabled="disabled"
                @input="input"
                @keydown.tab="tab"
                @keyup.enter="enter"
                @focusout="clearValue"
                @focus="handleFocus"
                ref="input"
            >
        </span>
        <span class="VInput__postfix">
            <slot name="postfix">

            </slot>
        </span>
    </span>
</template>

<script>
    export default {
        name: 'VInput',
        model: {
            prop: 'value',
            event: 'input',
        },
        props: {
            type: {
                type: String,
                default: 'text',
            },
            value: {
                type: [String, Number],
                required: false,
            },
            placeholder: {
                type: String,
                required: false,
                default: '',
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            isTabLikeEnter: {
                type: Boolean,
                default: false,
            },
            min: {
                type: Number,
                default: -Infinity,
            },
            max: {
                type: Number,
                default: Infinity,
            },
            isAllowFractional: {
                type: Boolean,
                default: false,
            },
            decimalCount: {
                type: Number,
                default: 2,
            },
        },
        data() {
            return {
                localValue: '',
            };
        },
        methods: {
            tab(event) {
                if (this.isTabLikeEnter) {
                    event.preventDefault();
                    this.$emit('tab');
                }
            },
            enter() {
                this.$emit('enter');
            },
            input() {
                this.$emit('change');
            },
            validateIsNumber(text) {
                return text !== null && this.type.toLowerCase() === 'number';
            },
            checkIsNeedUpdate() {
                return JSON.stringify(this.localValue) !== JSON.stringify(this.value);
            },
            handleFocus() {
                this.$emit('focus');
            },
            clearValue() {
                if (this.isAllowFractional && this.localValue.length) {
                    let value = this.localValue;
                    let formatted = value.trim();
                    if (formatted[formatted.length - 1] === '.') {
                        formatted = formatted.substring(0, formatted.length - 1);
                    }
                    this.localValue = formatted;
                }
            },
            formatNumber(text) {
                const trimmedText = text.trim();
                const formattedText = trimmedText;
                let number;
                if (this.isAllowFractional && formattedText.length) {
                    number = formattedText.match(/^[^.]*\.|[^.]+/g).join('').replace(/[^0-9.]/g, '');
                } else {
                    number = formattedText.replace(/[^0-9]/g, '');
                }

                if (number > this.max) {
                    return this.max;
                } else if (number < this.min) {
                    return this.min;
                }
                return number;
            },
        },
        watch: {
            value: {
                immediate: true,
                handler(newVal) {
                    if (this.checkIsNeedUpdate()) {
                        let newValFormatted = newVal;
                        if (this.validateIsNumber(newVal)) {
                            newValFormatted = this.formatNumber(newVal);
                        }
                        this.localValue = newValFormatted;
                    }
                },
            },
            localValue: {
                handler(newVal) {
                    if (this.checkIsNeedUpdate()) {
                        let newValFormatted = newVal;
                        if (this.validateIsNumber(newVal)) {
                            newValFormatted = this.formatNumber(newVal);
                        }
                        this.localValue = newValFormatted;
                        this.$emit('input', newValFormatted);
                    }
                },
            },
        },
    };
</script>

<style
    scoped
    lang="scss"
>
    .VInput {
        display: flex;

        &-wrapper {
            width: 100%;
        }

        &__prefix {
            display: flex;
            align-items: center;
        }

        input {
            height: 26px;
            padding-left: 10px;
            border-radius: 4px;

            &::placeholder {
                color: $primary-color;
                font-weight: 100;
                opacity: 0.7;
            }

            &:focus {
                outline: none;
            }

            border: 1px solid $primary-color;
        }
    }
</style>
